/* Collection-specific filtering for complete, small collections.
 * Shopify still renders the product cards, prices, links and sort order.
 * No product metadata, inventory or store settings are written here.
 */
(() => {
  'use strict';

  const PARAMS = { size: 'hjp_size', color: 'hjp_color', type: 'hjp_type' };
  const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];

  function optionIndex(product, key) {
    return product.options.findIndex((name) => {
      const normalized = String(name).trim().toLowerCase();
      return key === 'size'
        ? normalized.includes('size') || normalized === '尺码'
        : ['color', 'colour', '颜色'].includes(normalized);
    });
  }

  function valuesFor(product, key) {
    if (key === 'type') return [product.type];
    const index = optionIndex(product, key);
    return index < 0 ? [] : product.variants.map((variant) => variant[index]).filter(Boolean);
  }

  function matches(product, selected) {
    const types = selected.type || [];
    if (types.length && !types.includes(product.type)) return false;
    const sizes = selected.size || [];
    const colors = selected.color || [];
    if (!sizes.length && !colors.length) return true;
    const sizeIndex = optionIndex(product, 'size');
    const colorIndex = optionIndex(product, 'color');
    return product.variants.some((variant) =>
      (!sizes.length || sizes.includes(variant[sizeIndex])) &&
      (!colors.length || colors.includes(variant[colorIndex]))
    );
  }

  function getGroups(collection, products, enabled = true) {
    if (!enabled || products.length < 2 || collection === 'mystery-boxes') return [];
    let candidates;
    if (collection === 'clothing') {
      candidates = [{ key: 'size', label: 'Size' }, { key: 'color', label: 'Color' }];
    } else {
      const label = collection === 'toys' ? 'Toy type'
        : collection === 'accessories' ? 'Accessory type' : 'Product type';
      candidates = [{ key: 'type', label }];
    }
    return candidates.map((group) => {
      const values = [...new Set(products.flatMap((product) => valuesFor(product, group.key)))];
      values.sort((a, b) => {
        if (group.key === 'size') {
          const aIndex = SIZE_ORDER.indexOf(a.toUpperCase());
          const bIndex = SIZE_ORDER.indexOf(b.toUpperCase());
          if (aIndex >= 0 || bIndex >= 0) return (aIndex < 0 ? 100 : aIndex) - (bIndex < 0 ? 100 : bIndex);
        }
        return a.localeCompare(b, 'en', { numeric: true });
      });
      return { ...group, values };
    }).filter((group) => group.values.length > 1 && group.values.some((value) =>
      products.filter((product) => valuesFor(product, group.key).includes(value)).length < products.length
    ));
  }

  function readSelection(url, groups) {
    const selected = {};
    groups.forEach((group) => {
      selected[group.key] = [...new Set(url.searchParams.getAll(PARAMS[group.key])
        .filter((value) => group.values.includes(value)))];
    });
    return selected;
  }

  function selectionURL(href, selected) {
    const url = new URL(href);
    Object.values(PARAMS).forEach((param) => url.searchParams.delete(param));
    Object.entries(PARAMS).forEach(([key, param]) => {
      (selected[key] || []).forEach((value) => url.searchParams.append(param, value));
    });
    url.searchParams.delete('page');
    return url;
  }

  // Pure functions are also usable by the local validation script.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { matches, valuesFor, getGroups, readSelection, selectionURL };
  }
  if (typeof document === 'undefined') return;

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  class CollectionFilters {
    constructor(root) {
      this.root = root;
      this.products = [...root.querySelectorAll('[data-hjp-product]')].map((node) => ({
        ...JSON.parse(node.textContent), card: node.closest('.grid__item'),
      }));
      // Never filter a partial collection or incomplete metadata.
      if (this.products.length !== Number(root.dataset.hjpTotal) ||
          this.products.some((product) => !product.card || !Array.isArray(product.options) || !Array.isArray(product.variants))) {
        return;
      }
      this.groups = getGroups(root.dataset.hjpCollection, this.products, root.dataset.hjpFiltering === 'true');
      this.selected = readSelection(new URL(window.location.href), this.groups);
      this.open = null;
      this.controls = root.querySelector('.hjp-filter-groups');
      this.chips = root.querySelector('.hjp-filter-chips');
      this.panel = root.querySelector('.hjp-filter-panel');
      this.fields = root.querySelector('[data-hjp-fieldsets]');
      this.count = root.querySelector('.hjp-filter-count');
      this.done = root.querySelector('.hjp-filter-done');
      this.empty = root.querySelector('.hjp-filter-empty');
      this.grid = root.querySelector('ul.product-grid');
      this.caret = root.querySelector('[data-hjp-caret]');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      root.querySelectorAll('[data-hjp-clear]').forEach((button) => {
        button.addEventListener('click', () => this.clear(), options);
      });
      this.done.addEventListener('click', () => this.closePanel(), options);
      root.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.open) {
          event.preventDefault();
          this.closePanel();
        }
      }, options);
      const sort = root.querySelector('.hjp-filter-sort');
      if (sort) {
        sort.querySelector('select').addEventListener('change', () => sort.requestSubmit(), options);
        sort.addEventListener('submit', (event) => {
          event.preventDefault();
          const url = selectionURL(window.location.href, this.selected);
          url.searchParams.set('sort_by', sort.querySelector('select').value);
          window.location.assign(url.toString());
        }, options);
        sort.querySelector('[data-hjp-sort-apply]').hidden = true;
      }
      window.addEventListener('popstate', () => {
        this.selected = readSelection(new URL(window.location.href), this.groups);
        this.open = null;
        this.render();
      }, options);
      this.render();
    }

    selectedCount() {
      return Object.values(this.selected).reduce((sum, values) => sum + values.length, 0);
    }

    focusTrigger(key) {
      [...this.controls.querySelectorAll('button')].find((button) => button.dataset.key === key)?.focus();
    }

    updateURL() {
      const url = selectionURL(window.location.href, this.selected);
      if (url.toString() !== window.location.href) {
        window.history.pushState({ ...window.history.state, hjpCollectionFilters: true }, '', url.toString());
      }
    }

    clear() {
      this.selected = {};
      this.render();
      this.updateURL();
      this.controls.querySelector('button')?.focus();
    }

    closePanel() {
      const previous = this.open;
      this.open = null;
      this.render();
      this.focusTrigger(previous);
    }

    renderControls() {
      this.controls.replaceChildren();
      this.controls.hidden = this.groups.length === 0;
      this.groups.forEach((group) => {
        const n = (this.selected[group.key] || []).length;
        const button = element('button', 'hjp-filter-trigger');
        button.type = 'button';
        button.dataset.key = group.key;
        button.dataset.active = String(n > 0);
        button.setAttribute('aria-expanded', String(this.open === group.key));
        button.setAttribute('aria-controls', this.panel.id);
        button.append(element('span', '', group.label + (n ? ' · ' + n : '')));
        button.append(this.caret.content.cloneNode(true));
        button.addEventListener('click', () => {
          this.open = this.open === group.key ? null : group.key;
          this.render();
          this.focusTrigger(group.key);
        });
        this.controls.append(button);
      });
    }

    renderPanel() {
      this.fields.replaceChildren();
      this.panel.hidden = !this.open;
      if (!this.open) return;
      const group = this.groups.find((item) => item.key === this.open);
      if (!group) return;
      const fieldset = element('fieldset');
      fieldset.append(element('legend', '', group.label));
      const options = element('div', 'hjp-filter-options');
      group.values.forEach((value) => {
        const n = this.products.filter((product) => matches(product, { ...this.selected, [group.key]: [value] })).length;
        const label = element('label', 'hjp-filter-option');
        const input = element('input');
        input.type = 'checkbox';
        input.name = PARAMS[group.key];
        input.value = value;
        input.checked = (this.selected[group.key] || []).includes(value);
        input.disabled = n === 0 && !input.checked;
        input.setAttribute('aria-label', value + ', ' + n + (n === 1 ? ' product' : ' products'));
        input.addEventListener('change', () => {
          const selected = new Set(this.selected[group.key] || []);
          if (input.checked) selected.add(value);
          else selected.delete(value);
          this.selected[group.key] = [...selected];
          this.render();
          this.updateURL();
          [...this.fields.querySelectorAll('input')].find((replacement) => replacement.value === value)?.focus();
        });
        label.append(input, element('span', '', value), element('span', 'hjp-filter-option-count', String(n)));
        options.append(label);
      });
      fieldset.append(options);
      this.fields.append(fieldset);
    }

    renderChips() {
      this.chips.replaceChildren();
      this.chips.hidden = this.selectedCount() === 0;
      this.groups.forEach((group) => (this.selected[group.key] || []).forEach((value) => {
        const button = element('button', 'hjp-filter-chip', group.label + ': ' + value + ' ×');
        button.type = 'button';
        button.setAttribute('aria-label', 'Remove ' + group.label + ': ' + value);
        button.addEventListener('click', () => {
          this.selected[group.key] = this.selected[group.key].filter((item) => item !== value);
          this.render();
          this.updateURL();
          this.focusTrigger(group.key);
        });
        this.chips.append(button);
      }));
      if (this.selectedCount()) {
        const button = element('button', 'hjp-filter-clear', 'Clear all');
        button.type = 'button';
        button.addEventListener('click', () => this.clear());
        this.chips.append(button);
      }
    }

    render() {
      let visible = 0;
      this.products.forEach((product) => {
        const show = matches(product, this.selected);
        product.card.hidden = !show;
        if (show) {
          visible += 1;
          // A previously hidden lazy card must not remain in its scroll-reveal state.
          product.card.classList.add('scroll-trigger--cancel');
        }
      });
      if (this.empty) this.empty.hidden = visible > 0;
      if (this.grid) this.grid.hidden = visible === 0;
      this.count.textContent = this.selectedCount() || this.root.dataset.hjpNativeFiltered === 'true'
        ? visible + ' of ' + this.root.dataset.hjpAllTotal + ' products'
        : visible + (visible === 1 ? ' product' : ' products');
      this.done.textContent = 'Show ' + visible + (visible === 1 ? ' product' : ' products');
      this.renderControls();
      this.renderPanel();
      this.renderChips();
    }
  }

  function init(scope = document) {
    scope.querySelectorAll('.hjp-collection-browser').forEach((root) => {
      if (root.hjpCollectionFilters) return;
      try {
        root.hjpCollectionFilters = new CollectionFilters(root);
      } catch (error) {
        // Preserve the native cards and sorting if optional enhancement fails.
        root.querySelectorAll('.grid__item').forEach((card) => { card.hidden = false; });
        root.querySelectorAll('.hjp-filter-groups, .hjp-filter-panel, .hjp-filter-chips').forEach((node) => { node.hidden = true; });
        const grid = root.querySelector('ul.product-grid');
        if (grid) grid.hidden = false;
        const sortApply = root.querySelector('[data-hjp-sort-apply]');
        if (sortApply) sortApply.hidden = false;
        console.warn('Collection filters could not be initialized.', error);
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.hjp-collection-browser').forEach((root) => root.hjpCollectionFilters?.abort?.abort());
  });
})();
